import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { toast } from 'react-toastify';
import DataTable from './tables/DataTable';
import Tooltip from './Tooltip';

/**
 * Generic list + inline add-form + remove for small child-record
 * sub-resources (store mappings, product variants/images/pricing tiers,
 * etc). `columns` matches DataTable's shape; `addFields` is
 * [{ key, label, required, type, options, loadOptions, onSelect, hint }] for
 * the add-row form (`type: 'select'` renders a `<select>` from a static
 * `options: [{ value, label }]` array, or, for options that come from an
 * external system, `loadOptions: () => Promise<[{ value, label }]>` --
 * fetched once when the list mounts; otherwise a text input; `hint` renders
 * an info tooltip next to the label). `onSelect(option)` (select fields
 * only) returns a partial `{ key: value }` object merged into the form when
 * the user picks an option -- e.g. auto-filling a display-name field from
 * the selected option's label. `list`/`add`/`remove` are queries.js
 * functions already bound to the parent id by the caller.
 *
 * By default the add-form is always visible below the table. Pass
 * `collapsibleAdd` to hide it behind a "+" button below the table instead.
 *
 * `rowActions` (optional): [{ label, icon, onClick(row), hidden?(row) }] --
 * extra per-row icon buttons rendered before the built-in Remove action.
 * `icon` is a lucide-react component; `label` is used as the tooltip text
 * (and as a11y label). `onClick`'s result is awaited and the list is
 * refreshed afterward; `hidden` lets a consumer suppress the button for rows
 * it doesn't apply to.
 *
 * `canEdit` (default true): when false, the add form and all row actions
 * (remove, rowActions) are hidden -- for pages whose backend mutation is
 * restricted to a narrower role than the page's own view access (see
 * useCan). The list itself stays visible/read-only.
 *
 * `canRemoveRow(row)` (optional, default always true): per-row override that
 * hides just the built-in Remove button for rows that shouldn't be
 * individually deletable (e.g. ERP-synced rows meant to be read-only) while
 * still allowing other rows in the same list to be removed and the add-form
 * to stay usable -- unlike `canEdit`, which is all-or-nothing for the whole
 * list.
 */
export default function MappingList({
	list,
	add,
	remove,
	columns,
	addFields,
	idKey,
	emptyHeading,
	collapsibleAdd = false,
	onChange,
	rowActions,
	canEdit = true,
	canRemoveRow = () => true,
}) {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [form, setForm] = useState({});
	const [saving, setSaving] = useState(false);
	const [formOpen, setFormOpen] = useState(!collapsibleAdd);
	const [formKey, setFormKey] = useState(0);
	const [fieldOptions, setFieldOptions] = useState({});
	const [loadingOptions, setLoadingOptions] = useState({});

	useEffect(() => {
		addFields
			.filter((f) => f.loadOptions)
			.forEach((f) => {
				setLoadingOptions((prev) => ({ ...prev, [f.key]: true }));
				f.loadOptions().then((options) => {
					setFieldOptions((prev) => ({ ...prev, [f.key]: options || [] }));
					setLoadingOptions((prev) => ({ ...prev, [f.key]: false }));
				});
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const refresh = () => {
		setLoading(true);
		return list().then((result) => {
			if (result?.success) setRows(result.data);
			setLoading(false);
			onChange?.(result);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAdd = async (e) => {
		e.preventDefault();
		setSaving(true);
		const result = await add(form);
		setSaving(false);
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to add.');
			return;
		}
		setForm({});
		// File inputs can't be reset via React state alone -- remount the form
		// (native <input type=file> doesn't accept a controlled `value`) so a
		// previously-picked file doesn't appear still selected.
		setFormKey((k) => k + 1);
		refresh();
	};

	const handleRemove = async (id) => {
		await remove(id);
		refresh();
	};

	const handleRowAction = async (action, row) => {
		await action.onClick(row);
		refresh();
	};

	const tableColumns = canEdit
		? [
				...columns,
				...(rowActions || []).map((action, index) => ({
					key: `__action-${index}`,
					header: '',
					sortable: false,
					render: (_value, row) =>
						action.hidden?.(row) ? null : (
							<button
								type='button'
								aria-label={action.label}
								data-tooltip-id='mapping-list-actions-tooltip'
								data-tooltip-content={action.label}
								onClick={() => handleRowAction(action, row)}
							>
								<action.icon size={20} />
							</button>
						),
				})),
				{
					key: idKey,
					header: '',
					sortable: false,
					render: (id, row) =>
						canRemoveRow(row) ? (
							<button
								type="button"
								aria-label="Remove"
								data-tooltip-id='mapping-list-actions-tooltip'
								data-tooltip-content='Remove'
								onClick={() => handleRemove(id)}
							>
								<Trash2 size={20} />
							</button>
						) : null,
				},
			]
		: columns;

	const emptyHeadingContent =
		canEdit && collapsibleAdd && !formOpen ? (
			<>
				{emptyHeading}{' '}
				<button
					type='button'
					className='underline text-black hover:no-underline'
					onClick={() => setFormOpen(true)}
				>
					Add
				</button>
			</>
		) : (
			emptyHeading
		);

	return (
		<div className='max-w-5xl'>
			<DataTable columns={tableColumns} data={rows} loading={loading} emptyHeading={emptyHeadingContent} />

			{canEdit && collapsibleAdd && !formOpen && rows.length > 0 && (
				<button
					type='button'
					className='btn-secondary-small mt-5 rounded-full p-1.5'
					aria-label='Add'
					onClick={() => setFormOpen(true)}
				>
					<Plus className='size-4' />
				</button>
			)}

			{canEdit && formOpen && (
				<form key={formKey} onSubmit={handleAdd} className='flex flex-wrap items-end gap-3 mt-5'>
					{addFields.map((f) => (
						<div key={f.key} className='flex flex-col'>
							<label className='flex items-center gap-1'>
								{f.label}
								<Tooltip content={f.hint} />
							</label>
							{f.type === 'select' ? (
								<select
									className='form-input w-48'
									required={f.required}
									disabled={f.loadOptions && loadingOptions[f.key]}
									value={form[f.key] ?? ''}
									onChange={(e) => {
										const value = e.target.value;
										const options = f.loadOptions ? fieldOptions[f.key] || [] : f.options;
										const selected = options.find((o) => String(o.value) === value);
										setForm((prev) => ({ ...prev, [f.key]: value, ...(f.onSelect ? f.onSelect(selected) : {}) }));
									}}
								>
									<option value=''>{f.loadOptions && loadingOptions[f.key] ? 'Loading...' : 'Select...'}</option>
									{(f.loadOptions ? fieldOptions[f.key] || [] : f.options).map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
							) : f.type === 'file' ? (
								<input
									type='file'
									accept={f.accept}
									className='form-input w-72 pt-1.5'
									required={f.required}
									onChange={(e) => setForm({ ...form, [f.key]: e.target.files?.[0] || null })}
								/>
							) : (
								<input
									className='form-input w-72'
									required={f.required}
									value={form[f.key] ?? ''}
									onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
								/>
							)}
						</div>
					))}
					<button type='submit' disabled={saving} className='btn-primary h-10'>
						{saving ? 'Adding...' : 'Add'}
					</button>
					{collapsibleAdd && (
						<button
							type='button'
							className='btn-secondary h-10'
							onClick={() => {
								setFormOpen(false);
								setForm({});
							}}
						>
							Cancel
						</button>
					)}
				</form>
			)}

			<ReactTooltip id='mapping-list-actions-tooltip' />
		</div>
	);
}
